import React, { useContext, useLayoutEffect, useMemo, useState } from 'react';

import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import { RadarChart, RadarChartData } from '@/components/shared/RadarChart';
import { CHART_PALETTE } from '@/helpers/chartPalette';
import { ChartSectionData } from '@/helpers/reportsDataHelpers';
import { RadarDataPoint } from '@/hooks/useReportsData';
import { GridLabelProps, RadarCustomLayerProps } from '@nivo/radar';

import { ChartEmptyState } from '../ChartEmptyState/ChartEmptyState';
import styles from './FeedbackRadarChart.module.scss';

function toTitleCase(s: string): string {
	return s.replace(/\b(?<!')\w/g, (c) => c.toUpperCase());
}

function hexToRgba(hex: string, alpha: number): string {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r},${g},${b},${alpha})`;
}

type TooltipDatum = { color: string; id: string; value: number };

function RadarTooltipContent({
	index,
	data,
}: {
	index: string | number;
	data: TooltipDatum[];
}) {
	return (
		<div
			style={{
				background: 'white',
				padding: '8px 12px',
				borderRadius: 4,
				boxShadow: '0 2px 8px rgba(0,0,0,.12)',
				fontSize: 12,
				lineHeight: 1.6,
				minWidth: 140,
			}}
		>
			<div style={{ fontWeight: 600, marginBottom: 4 }}>
				{String(index)}
			</div>
			{data.map((d) => (
				<div
					key={d.id}
					style={{ display: 'flex', alignItems: 'center', gap: 6 }}
				>
					<span
						style={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							background: d.color,
							flexShrink: 0,
							display: 'inline-block',
						}}
					/>
					<span style={{ flex: 1 }}>{d.id}</span>
					<span style={{ fontWeight: 500, marginLeft: 8 }}>
						{d.value.toFixed(1)}
					</span>
				</div>
			))}
		</div>
	);
}

const RadarHoveredAxisContext = React.createContext<string | null>(null);

const RadarGridLabel: React.FC<GridLabelProps> = ({ id, anchor, x, y }) => {
	const hoveredAxis = useContext(RadarHoveredAxisContext);
	return (
		<g transform={`translate(${x}, ${y})`}>
			<text
				dominantBaseline="central"
				textAnchor={anchor}
				style={{
					fill: hoveredAxis === id ? '#23263b' : '#777aaf',
					fontSize: 11,
					fontFamily: 'inherit',
					transition: 'fill 0.15s ease',
				}}
			>
				{id}
			</text>
		</g>
	);
};

type CrosshairLayerProps = RadarCustomLayerProps<Record<string, unknown>>;
const RadarCrosshairLayer: React.FC<CrosshairLayerProps> = ({
	centerX,
	centerY,
	radiusScale,
	angleStep,
	indices,
}) => {
	const hoveredAxis = useContext(RadarHoveredAxisContext);
	if (!hoveredAxis) return null;
	const idx = (indices as string[]).indexOf(hoveredAxis);
	if (idx < 0) return null;
	const radius = radiusScale.range()[1];
	const angle = idx * angleStep - Math.PI / 2;
	const tipX = Math.cos(angle) * radius;
	const tipY = Math.sin(angle) * radius;
	return (
		<g transform={`translate(${centerX}, ${centerY})`}>
			<line
				x1={0}
				y1={0}
				x2={tipX}
				y2={tipY}
				style={{
					stroke: '#23263b',
					strokeWidth: 1,
					strokeOpacity: 1,
					pointerEvents: 'none',
				}}
			/>
		</g>
	);
};

type Props = {
	title: string;
	section: ChartSectionData<RadarDataPoint>;
	colorMap?: Record<string, string>;
};

export const FeedbackRadarChart: React.FC<Props> = ({
	title,
	section,
	colorMap,
}) => {
	const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
	const [hoveredAxis, setHoveredAxis] = useState<string | null>(null);
	const [legendTooltip, setLegendTooltip] = useState<{
		seg: string;
		x: number;
		y: number;
	} | null>(null);

	const segments = useMemo(
		() => section.data.map((d) => d.segment),
		[section.data],
	);

	const displaySegments = useMemo(
		() => segments.map(toTitleCase),
		[segments],
	);

	// Axes = the 3 feedback lenses; each series line = one segment (generation / service / gender / skill)
	const nivoData: RadarChartData = useMemo(
		() => [
			{
				lens: 'Personal',
				...Object.fromEntries(
					section.data.map((d, i) => [
						displaySegments[i],
						parseFloat(d.personalScore.toFixed(2)),
					]),
				),
			},
			{
				lens: 'Team',
				...Object.fromEntries(
					section.data.map((d, i) => [
						displaySegments[i],
						parseFloat(d.teamScore.toFixed(2)),
					]),
				),
			},
			{
				lens: 'Congregational',
				...Object.fromEntries(
					section.data.map((d, i) => [
						displaySegments[i],
						parseFloat(d.congregationalScore.toFixed(2)),
					]),
				),
			},
		],
		[section.data, displaySegments],
	);

	const baseColors = useMemo(
		() =>
			segments.map(
				(seg, i) =>
					colorMap?.[seg.toLowerCase()] ??
					CHART_PALETTE[i % CHART_PALETTE.length],
			),
		[segments, colorMap],
	);

	const colors = useMemo(
		() =>
			baseColors.map((hex, i) => {
				if (!hoveredLabel || hoveredLabel === displaySegments[i])
					return hex;
				return hexToRgba(hex, 0.15);
			}),
		[baseColors, displaySegments, hoveredLabel],
	);

	// Reorder keys/colors so the hovered segment renders last (on top) in SVG paint order
	const orderedKeys = useMemo(() => {
		if (!hoveredLabel) return displaySegments;
		return [
			...displaySegments.filter((s) => s !== hoveredLabel),
			hoveredLabel,
		];
	}, [displaySegments, hoveredLabel]);

	const orderedColors = useMemo(() => {
		if (!hoveredLabel) return colors;
		const paired = displaySegments.map((seg, i) => ({
			seg,
			color: colors[i],
		}));
		return [
			...paired.filter((p) => p.seg !== hoveredLabel).map((p) => p.color),
			paired.find((p) => p.seg === hoveredLabel)!.color,
		];
	}, [displaySegments, colors, hoveredLabel]);

	const sliceTooltip = useMemo(() => {
		const colorBySegment = new Map(
			displaySegments.map((seg, i) => [seg, baseColors[i]]),
		);
		return function RadarSliceTooltip({
			index,
			data,
		}: {
			index: string | number;
			data: Array<{ color: string; id: string; value: number }>;
		}) {
			useLayoutEffect(() => {
				const axis = String(index);
				setHoveredAxis(axis);
				return () =>
					setHoveredAxis((prev) => (prev === axis ? null : prev));
			}, [index]);
			const processedData = [...data]
				.sort((a, b) => b.value - a.value)
				.map((d) => ({
					...d,
					color: colorBySegment.get(d.id) ?? d.color,
				}));
			return React.createElement(RadarTooltipContent, {
				index,
				data: processedData,
			});
		};
	}, [displaySegments, baseColors]);

	if (section.emptyReason !== null || section.data.length < 1) {
		return (
			<ModalSectionCard paddingSize="lg">
				<ModalSectionTitle>{title}</ModalSectionTitle>
				<ChartEmptyState
					emptyReason={section.emptyReason ?? 'no-history'}
				/>
			</ModalSectionCard>
		);
	}

	return (
		<ModalSectionCard paddingSize="lg">
			<ModalSectionTitle>{title}</ModalSectionTitle>
			<RadarHoveredAxisContext.Provider value={hoveredAxis}>
				<RadarChart
					data={nivoData}
					keys={orderedKeys}
					indexBy="lens"
					maxValue={5}
					colors={orderedColors}
					fillOpacity={0}
					enableDots={true}
					dotSize={10}
					dotColor={{ from: 'color' }}
					dotBorderWidth={2}
					dotBorderColor="#ffffff"
					borderWidth={2}
					isInteractive={true}
					sliceTooltip={sliceTooltip}
					gridLabel={RadarGridLabel}
					layers={[
						'grid',
						'layers',
						'slices',
						RadarCrosshairLayer,
						'dots',
						'legends',
					]}
					theme={{
						text: { fill: '#23263b' },
						grid: { line: { stroke: '#d6d6e7' } },
						crosshair: {
							line: {
								stroke: '#23263b',
								strokeWidth: 1,
								strokeOpacity: 1,
								strokeDasharray: '0',
							},
						},
					}}
					margin={{ bottom: 50, left: 50, right: 50, top: 25 }}
					wrapperClassName={styles['chart']}
				/>
			</RadarHoveredAxisContext.Provider>
			<div className={styles['legend']}>
				{displaySegments.map((seg, i) => (
					<div
						key={seg}
						className={styles['legend-item']}
						style={{
							opacity:
								hoveredLabel && hoveredLabel !== seg ? 0.3 : 1,
							cursor: 'default',
						}}
						onMouseEnter={(e) => {
							setHoveredLabel(seg);
							const r = e.currentTarget.getBoundingClientRect();
							setLegendTooltip({
								seg,
								x: r.left + r.width / 2,
								y: r.top,
							});
						}}
						onMouseLeave={() => {
							setHoveredLabel(null);
							setLegendTooltip(null);
						}}
					>
						<div
							className={styles['legend-circle']}
							style={{ background: baseColors[i] }}
						/>
						<span className={styles['legend-label']}>{seg}</span>
					</div>
				))}
			</div>
			{legendTooltip &&
				(() => {
					const data: TooltipDatum[] = nivoData.map((entry) => ({
						id: String(entry['lens']),
						color:
							baseColors[
								displaySegments.indexOf(legendTooltip.seg)
							] ?? '#000',
						value: (entry[legendTooltip.seg] as number) ?? 0,
					}));
					return (
						<div
							style={{
								position: 'fixed',
								left: legendTooltip.x,
								top: legendTooltip.y - 8,
								transform: 'translate(-50%, -100%)',
								zIndex: 9999,
								pointerEvents: 'none',
							}}
						>
							<RadarTooltipContent
								index={legendTooltip.seg}
								data={data}
							/>
						</div>
					);
				})()}
		</ModalSectionCard>
	);
};
